<template>
    <div class="tab-pane fade" :class="{'show active': current_step === 2}" id="create-project-step" role="tabpanel" aria-labelledby="create-project-step-tab">
        <div class="create-project">
            <!-- user not authenticated: Login or register -->
            <div class="user-logged-out animated fadeIn" v-if="!authenticated">
                <!-- Login or Register selection-->
                <div class="container-fluid px-0 mx-0 lst-login-register-selection my-5">
                    <div class="row">
                        <div class="col-sm-12 text-left">
                            <h3 class="text-white">{{ $t('Please sign in or create a free account') }}</h3>
                            <p class="text-light">{{ $t('As soon as you have signed in, the product configured can be saved and you can continue the line lighting or track system planning. Prices will be visible after signing in, as well.') }}</p>
                            <p class="text-light">{{ $t('The registration is free of charge and without any obligations. You can delete your account anytime.') }}</p>
                        </div>
                    </div>
                    <div class="row mt-3">
                        <div class="col-6">
                            <div class="custom-control custom-radio image-checkbox text-left">
                                <input v-model="loginOrRegister" value="login" type="radio" class="custom-control-input" id="login-or-register-2" name="login-or-register">
                                <label class="custom-control-label btn btn-dark login-button" :class="{ 'active': loginOrRegister === 'login'}" for="login-or-register-2">
                                    <i class="fa fa-user-check mb-2" aria-hidden="true"></i>
                                    <span class="text-white">{{ $t('Sign in') }}</span>
                                </label>
                            </div>
                        </div>
                        <div class="col-6">
                            <div class="custom-control custom-radio image-checkbox text-left">
                                <input v-model="loginOrRegister" value="register" type="radio" class="custom-control-input" id="login-or-register-1" name="login-or-register">
                                <label class="custom-control-label btn btn-dark register-button" :class="{ 'active': loginOrRegister === 'register'}" for="login-or-register-1">
                                    <i class="fa fa-user-plus mb-2" aria-hidden="true"></i>
                                    <span class="text-white">{{ $t('Register now') }}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
                <!-- Login Form-->
                <div v-if="loginOrRegister === 'login'" class="mb-5">
                    <h3 class="text-white">{{ $t('Sign in') }}</h3>
                    <login-form></login-form>
                </div>
                <!-- Register Form -->
                <div v-if="loginOrRegister === 'register'" class="mb-5">
                    <h3 class="text-white">{{ $t('No account? Register now') }}</h3>
                    <register-form></register-form>
                </div>
            </div>
            <!-- user authenticated: create project/planning -->
            <div v-else class="user-logged-in animated fadeIn">
                <div class="container-fluid px-0 mx-0 lst-login-register-selection my-5" v-if="!projectCreated && !planningCreated">
                    <div class="row">
                        <div class="col-12 text-left">
                            <h3 class="text-white">{{ $t('Your project') }}</h3>
                            <p class="text-light">{{ $t('Create a new project or plan a lighting line or track system as part of an existing project.') }}</p>
                        </div>
                    </div>
                    <div class="row mt-3">
                        <div class="col-6">
                            <div class="custom-control custom-radio image-checkbox text-left">
                                <input v-model="newOrExistingProject" value="new-project" type="radio" class="custom-control-input" id="new-project" name="new-project">
                                <label class="custom-control-label btn btn-dark login-button" :class="{ 'active': newOrExistingProject === 'new-project'}" for="new-project">
                                    <i class="fa fas fa-folder-plus" aria-hidden="true"></i>
                                    <span class="text-white">{{ $t('Create new project') }}</span>
                                </label>
                            </div>
                        </div>
                        <div class="col-6">
                            <div class="custom-control custom-radio image-checkbox text-left" v-if="projects.length > 0 && activeProjects.length > 0">
                                <input v-model="newOrExistingProject" value="existing-project" type="radio" class="custom-control-input" id="existing-project" name="existing-project">
                                <label class="custom-control-label btn btn-dark register-button" :class="{ 'active': newOrExistingProject === 'existing-project'}" for="existing-project">
                                    <i class="fas fa-folder-open" aria-hidden="true"></i>
                                    <span class="text-white">{{ $t('Use existing project') }}</span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- PROJECT & PLANNING SECTION -->
                <div class="container-fluid px-0 mx-0 lst-login-register-selection my-5">
                    <!-- CREATE PROJECT -->
                    <div class="row" v-if="newOrExistingProject === 'new-project' && !projectCreated && !planningCreated">
                        <div class="col-12 text-left">
                            <form @submit.prevent="submitProject" class="create-project-form">
                                <div class="form-group">
                                    <input v-model="projectFormFields.title" type="text" name="title" class="form-control" :placeholder="$t('Project name')" :disabled="projectCreated">
                                    <div class="alert alert-danger" v-if="projectFormErrors && projectFormErrors.title">
                                        {{ projectFormErrors.title[0] }}
                                    </div>
                                </div>
                                <button type="submit" class="btn btn-primary" :disabled="projectFormSubmitting">
                                    <span v-if="!projectFormSubmitting">{{ $t('Create project') }}</span>
                                    <span v-else>{{ $t('Save') }}...</span>
                                </button>
                            </form>
                        </div>
                    </div>

                    <!-- CREATE PLANNING -->
                    <div class="row" v-if="( newOrExistingProject === 'existing-project' && projects.length > 0  && activeProjects.length > 0 ) || ( newOrExistingProject === 'new-project' && projectCreated && planningFormFields.project_id !== '' )">
                        <div class="col-12 text-left">
                            <h3 class="text-white">{{ $t('Create line lighting or track system planning') }}</h3>
                            <p class="text-light">{{ $t('You can create multiple lighting lines or track systems per project.') }}</p>
                            <form @submit.prevent class="create-planning-form">
                                <div class="form-group" v-if="projects.length > 0  && activeProjects.length > 0 && !projectCreated && activeProjects">
                                    <select class="form-control" v-model="planningFormFields.project_id" id="select-project" :disabled="projectCreated || planningCreated">
                                        <option disabled>-- {{ $t('Choose') }} --</option>
                                        <option v-for="project in activeProjects" :value="project.id">{{ project.title }}</option>
                                    </select>
                                </div>
                                <div class="form-group">
                                    <input v-model="planningFormFields.title" type="text" name="title" class="form-control" :placeholder="$t('Name of planning')" :disabled="planningCreated">
                                    <div class="alert alert-danger" v-if="planningFormErrors && planningFormErrors.title">
                                        {{ planningFormErrors.title[0] }}
                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>

    import { mapGetters } from 'vuex';

    export default {
        props: [
            'current_step',
            'product_configurator_id',
            'projects',
            'planningCreated',
            'planningFormErrors'
        ],
        data() {
            return {
                /**
                 * PROJECT DATA
                 **/
                projectFormFields: {
                    title: '',
                },
                projectCreated: false,
                projectFormErrors: {},
                projectFormSubmitting: false,

                loginOrRegister: '',
                newOrExistingProject: '',

                /**
                 * PLANNING DATA
                 **/
                planningFormFields: {
                    title: '',
                    product_configurator_id: '',
                    project_id: '',
                },
            }
        },

        methods: {

            submitProject() {
                this.projectFormSubmitting = true;
                this.$emit('update-loading', true);

                axios.post('/api/projects', this.projectFormFields)
                    .then(response => {
                        this.projectFormSubmitting = false;
                        this.projectCreated = true;

                        this.planningFormFields.project_id = response.data.data.id;
                        this.$emit('get-projects', true);
                        // this.loading = false; set in getProjects

                    }).catch(error => {
                    if (error.response.status === 422) { // Validation Error
                        this.projectFormErrors = error.response.data.errors;
                        this.projectFormSubmitting = false;
                        this.$emit('update-loading', false);
                    }
                });
            },

        },

        computed: {

            ...mapGetters({
                authenticated: 'auth/authenticated',
                user: 'auth/user',
                permissions: 'auth/permissions'
            }),

            activeProjects() {
                return this.projects.filter(project => !this.$project_plannings_need_update(project.plannings));
            },

            /**
             * GENERAL COMPUTED
             **/

            stepTwoValidated() {

                let step_2_validated = false;

                // Validate Step 2

                if ( this.newOrExistingProject === ''
                    || ( this.newOrExistingProject === 'new-project' && ( !this.projectCreated || this.planningFormFields.title === '') )
                    || ( this.newOrExistingProject === 'existing-project' && ( this.planningFormFields.title === '' || this.planningFormFields.project_id === '' )) ) {
                    step_2_validated = 'step_2_not_valid';
                } else {
                    step_2_validated = 'step_2_valid';
                }

                return step_2_validated;

            },


        },

        watch: {
            planningFormFields: {
                handler(val, oldVal){
                    this.$emit('update-planning-form-fields', val);
                },
                deep: true
            },
            stepTwoValidated: function(value) {
                this.$emit('update-step-2-validated', value);
            },
            product_configurator_id: function(value) {
                this.planningFormFields.product_configurator_id = value;
            }
        },
    }
</script>
